import React, { useEffect, useRef, useState } from "react";
import { useHistory} from "react-router";
import { usePersistentState, markNavigationPreserve, consumeNavigationPreserve, clearNamespace } from "../../hooks/persistence";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { Service } from "../../Service/ApiService";
import { CommonService } from "../../Service/CommonService";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useRoute } from "../Common/route";
import Constant from "../../Constant";
import SearchIcon from "@mui/icons-material/Search";
import { SupportApiService } from "../../Service/SupportApiService";
import { Dropdown, Form } from "react-bootstrap";
import DataGridLoader from "../Common/DataGridLoader";

interface LocationState {
    data: string;
    excludeStatus: Boolean;
}
interface AllRequestProps {
    statusData?: string;
    excludeStatus?: boolean;
    embedded?: boolean;
    title?: string;
}

const AllRequest: React.FC<AllRequestProps> = ({
    statusData,
    excludeStatus = false,
    embedded = false,
    title
}) => {
    const route = useRoute();
    const history = useHistory();
    const controllerRef = useRef<AbortController | null>(null);
    const state = history.location.state as LocationState;
    const activeStatusData = statusData ?? state?.data ?? "SEND_TO_WAREHOUSE";
    const activeExcludeStatus = statusData !== undefined ? excludeStatus : Boolean(state?.excludeStatus);
    const [allRequests, setAllRequests] = useState<any[]>([]);
    const [services, setServices] = useState([
        { "id": 1, "serviceName": "Order Request" },
        { "id": 2, "serviceName": "Offer Request" }
    ]);
    const [isLoading, setIsLoading] = useState(true);
    const localeText = {
        noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
    };
    const [totalCount, setTotalCount] = useState(0);
    const statusLabelMap = {
        'PENDING_REVIEW': "Pending Review",
        'SEND_TO_WAREHOUSE': "Pending Review",
        'CART, PENDING_REVIEW, REASSIGN_TO_ADMIN, WAREHOUSE_COMPLETED, ADMIN_ACCEPTED_FOR_WAREHOUSE, REJECTED, CLOSED': "In Process",
        'IN_PROCESS, CONFIRMED, IN_STAGE, CONFIRMED_FOR_WAREHOUSE, IN_STAGE_FOR_WAREHOUSE': "In Process",
        'REASSIGN_TO_ADMIN, WAREHOUSE_COMPLETED, ADMIN_ACCEPTED_FOR_WAREHOUSE': "Awaiting Admin Action",
        'REJECTED, CLOSED': "Closed"
    };
    const defaultFilter = {
        requestType: null,
        requestNumber: null,
        companyName: null,
        createdBy: null,
        email: null,
        modifiedDate: null,
        modifiedOn: null
    };

    const _filterInitRef = useRef(false);
    if (!_filterInitRef.current) {
        _filterInitRef.current = true;
        if (!consumeNavigationPreserve("all-request")) {
            clearNamespace("all-request");
        }
    }

    const [page, setPage] = usePersistentState<number>("all-request", "page", 0);
    const [pageSize, setPageSize] = usePersistentState<number>("all-request", "pageSize", 50);
    const [sortField, setSortField] = usePersistentState<string>("all-request", "sortField", "modifiedDate");
    const [sortDirection, setSortDirection] = usePersistentState<string>("all-request", "sortDirection", "desc");
    const [filter, setFilter] = usePersistentState<any>("all-request", "filter", defaultFilter);

    useEffect(() => {
        fetchAllRequestsForWarehouse(page, pageSize, filter, sortField, sortDirection);
        fetchAllServices();
    }, []);



      // For Request Type Dropdown
      const [showReqType, setShowReqType] = useState(false);
      const dropdownRef = useRef(null);
    
      const handleSelect = (event, value) => {
        event.stopPropagation(); // stop parent click
        handleFilterChange({ target: { value } }, "requestType");
        setShowReqType(false); // ✅ close after selecting
      };
    
      // ✅ Close when clicking outside
      useEffect(() => {
        const handleClickOutside = (event) => {
          if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowReqType(false);
          }
        };
    
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }, []);
    // For Request Dropdown End
    

    const fetchAllRequestsForWarehouse = async (page: number, size: number, filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;

            // Cancel the previous request if any
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            // Store the current controller to cancel the request if needed
            controllerRef.current = controller;

        try {
            setIsLoading(true);
            setAllRequests([]);

            // Important: Adjust the page number for backend (if it uses 0-based indexing)
            const response: any = await Service.fetchAllRequestsForWarehouse(
                activeStatusData,
                activeExcludeStatus,
                CommonService.getUserId(),
                page,
                size,
                filter,
                sortField,
                sortDirection,
                signal
            );
            setAllRequests(response.data.content);
            setTotalCount(response.data.totalElements);
            setIsLoading(false);
        } catch (error) {
            if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
                return;
            }
            setIsLoading(false);
            console.error("Error fetching data:", error);
        }
    };

    const fetchAllServices = async () => {
        try {
            setIsLoading(true);

            const response = await SupportApiService.getAllServiceType();

            let filteredServices = response.data;

            // Remove Barrel Valuation service for Warehouse users
            if (CommonService.isWarehouse()) {
                filteredServices = response.data.filter(
                    (service) =>
                        service.serviceName !== Constant.CustomerService.BARREL_VALUATION
                );
            }

            const updatedServices = [
                ...services, // existing hardcoded services
                ...filteredServices,
            ];

            setServices(updatedServices);
        } catch (error) {
            console.log("Error occurred while API calling:", error);
        } finally {
            setIsLoading(false);
        }
    };


    const handleFilterChange = (event: any, field: keyof any) => {
        const value = event.target.value;

        const tempFilter = {
            ...filter,
            [field]: value
        };

        // Update the filter state
        setFilter(tempFilter);

        fetchAllRequestsForWarehouse(page, pageSize, tempFilter, sortField, sortDirection);
    };

    const CellWithTooltip = ({ value }: { value: string | number }) => {
        return (
            <Tooltip arrow title={value?.toString()}>
                <span>{value?.toString()}</span>
            </Tooltip>
        );
    };

    const tableDataWithIds = allRequests.map((rowData, index) => ({
        ...rowData,
        id: rowData.requestId
    }));

    const onPaginationModelChange = (newPaginationModel) => {
        setPage(newPaginationModel.page);
        setPageSize(newPaginationModel.pageSize);

        // Fetch using the latest sorting values
        fetchAllRequestsForWarehouse(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
    };

    const handleSortChange = (sortModel) => {
        if (sortModel.length > 0) {
            const newSortField = sortModel[0].field;
            const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

            setSortField(newSortField);
            setSortDirection(newSortDirection);

            fetchAllRequestsForWarehouse(page, pageSize, filter, newSortField, newSortDirection);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "requestType",
            headerName: "Request Type",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 250,
            sortable: true,
            renderHeader: (params) => (
                <>
                    <div>
                        <Typography
                            variant="body2"
                            sx={{ color: "#6F6F6F", fontSize: "14px" }}
                        >
                            {params.colDef.headerName}
                        </Typography>
                        <div
              ref={dropdownRef}
              className="position-relative"
              style={{ width: "200px" }}
              onClick={(e) => e.stopPropagation()} // prevent bubbling to parents
            >
              <Dropdown show={showReqType}>
                <Dropdown.Toggle
                  variant="light"
                  className="w-100 border mt-1 form-select custom-dropdown-toggle text-start"
                  id="dropdown-custom-select"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowReqType((prev) => !prev); // ✅ toggle open/close
                  }}
                >
                  {filter.requestType || "Select Request Type"}
                </Dropdown.Toggle>

                {showReqType && (
                  <Dropdown.Menu className="custom-dropdown-menu w-100 show">
                    {services.map((service) => (
                      <Dropdown.Item
                        key={service.id}
                        onClick={(event) => handleSelect(event, service.serviceName)}
                        active={filter.requestType === service.serviceName}
                      >
                        {service.serviceName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                )}
              </Dropdown>
            </div>
                    </div>
                </>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.row.requestType === 'Customer Sample Request' ? 'Sample Request' : params.row.requestType} />
            ),
        },
        {
            field: "requestNumber",
            headerName: "Request Number",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 250,
            sortable: true,
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "200px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            //value={filter.requestNumber}
                            defaultValue={filter.requestNumber}
                            onChange={(event) => handleFilterChange(event, "requestNumber")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.row.requestNumber || "-"} />
            ),
        },
        {
            field: "companyName",
            headerName: "Account Owner Name",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 250,
            sortable: true,
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "200px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            //value={filter.companyName}
                            defaultValue={filter.companyName}
                            onChange={(event) => handleFilterChange(event, "companyName")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />,
        },
        {
            field: "createdBy",
            headerName: "User",
            flex: 1,
            minWidth: 250,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "200px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            //value={filter.createdBy}
                            defaultValue={filter.createdBy}
                            onChange={(event) => handleFilterChange(event, "createdBy")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => (
                <div className="d-flex gap-2 align-items-center">
                    {/* <div className="user-img-wrapper">
                        <img src={AlterImage} alt="CompanyIcn" />
                    </div> */}
                    <span>
                        <CellWithTooltip value={params.value} />
                    </span>
                </div>
            ),
        },
        {
            field: "email",
            headerName: "Email",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 250,
            sortable: true,
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "200px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            //value={filter.email}
                            defaultValue={filter.email}
                            onChange={(event) => handleFilterChange(event, "email")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />,
        },
        {
            field: "modifiedDate",
            headerName: "Modified At",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 250,
            sortable: true,
            valueGetter: (value: any, row: any) => {
                const dateTimeString = row.modifiedDate ? row.modifiedDate : null;
                if (dateTimeString) {
                    return CommonService.isUser() &&
                        !CommonService.isSameCompany(row.companyId)
                        ? CommonService.formatStrictDate(dateTimeString)
                        : CommonService.formatDateTime(dateTimeString);
                }
            },
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "200px" }}>
                        <input
                            type="date"
                            className="form-control border mt-1"
                            style={{ boxShadow: "none" }}
                            id="modifiedOn"
                            name="modifiedOn"
                            placeholder="MM/DD/YYYY"
                            //value={filter.modifiedOn}
                            defaultValue={filter.modifiedOn}
                            onChange={(event) => handleFilterChange(event,"modifiedOn")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />,
        },
    ].filter(Boolean) as GridColDef[];

    const handleRowClick = (params) => {
        let request = params.row.requestType.replace(/\s+/g, '-').toLowerCase();
        if(params.row.requestType === "Order Request")
            request = 'order-detail';
        else if(params.row.requestType === "Offer Request")
            request = `view-${request}`;
        else if(params.row.requestType === "Sample Request")
            request = `view-${request}`;
        else if(params.row.requestType === "Customer Sample Request")
            request = "view-customer-service-request";
        else if (Object.values(Constant.CustomerService).includes(params.row.requestType))
            request = "view-customer-service-request";

        markNavigationPreserve("all-request");
        history.push(`/master/${request}/${params.row.id}`);
    }

    return (
        <>
            <div className={CommonService.isAdmin() ? "usersList pb-5" : "usersList p-0"}>
                <div className="usersList__header--wrapper d-flex flex-wrap align-items-center">
                    {embedded ? (<>
                        <h5 className="mb-0">
                            <b>{title || statusLabelMap[state?.data] || "All Requests"}</b>
                        </h5>
                    </>
                    ) : 
                    (
                        <>
                            <IconButton
                                onClick={() => CommonService.isAdmin() ? route('/master/service-requests-and-queue') : route('/master/storage-dashboard')}
                                aria-label="ArrowBackIcon"
                                sx={{
                                    backgroundColor: "#FFF",
                                    "&:hover": { backgroundColor: "#FFF" },
                                }}
                            >
                                <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                            </IconButton>

                            <div className="header--title me-2">
                                <h1 className="title mb-0 ms-2">{title || statusLabelMap[state?.data] || "All Requests"}</h1>
                            </div>
                        </>
                    )}
                </div>
                <div className="user-table-container">
                    <div className="userTable">
                        <DataGrid
                            localeText={localeText}
                            rows={tableDataWithIds}
                            columns={columns}
                            className="data-grid support-req--listing"
                            paginationModel={{ page, pageSize }}
                            pagination
                            paginationMode="server"
                            sortingMode="server"
                            rowCount={totalCount}
                            onSortModelChange={handleSortChange}
                            onPaginationModelChange={onPaginationModelChange}
                            disableRowSelectionOnClick={true}
                            getRowId={(row) => row.requestId}
                            loading={isLoading}
                            slots={{
                                loadingOverlay: DataGridLoader,
                            }}
                            pageSizeOptions={[10, 20, 50, 100]}
                            disableColumnResize
                            onRowClick={handleRowClick}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default AllRequest;